import { ECHO_SNR_DB, NEAR_FIELD_BINS } from "utils/radarSignal";
import { LIVE_COLORS } from "./RadarSpectrum";

// Radar live: four small trends over the waterfall's window, one point per
// column, on the same time axis as the waterfall above them. Rich wakes adds
// a fifth (flicker) for its burst history, one point per burst.
const W = 300;
const H = 48;

// An SVG path through the points, scaled to the time window and the values'
// own range, with gaps where a value is missing or where no capture came for
// over three typical spacings (at least a minute), as on the waterfall.
// Points without the key at all (another series' source) are left out.
export function sparkPath(all, from, to, key) {
  // Points from another source (without this key at all) are not gaps.
  const points = all.filter((p) => p[key] !== undefined);
  const values = points.map((p) => p[key]).filter((v) => Number.isFinite(v));
  if (!values.length || !(to > from)) return { d: "", min: null, max: null };
  let min = Math.min(...values);
  let max = Math.max(...values);
  if (max - min < 1e-9) {
    min -= 1;
    max += 1;
  }
  const spacing = points
    .slice(1)
    .map((p, i) => p.ms - points[i].ms)
    .filter((g) => g > 0)
    .sort((a, b) => a - b);
  const hold = Math.max(
    60000,
    3 * (spacing.length ? spacing[Math.floor(spacing.length / 2)] : 30000),
  );
  let d = "";
  let last = null;
  for (const p of points) {
    const v = p[key];
    if (!Number.isFinite(v) || p.ms < from || p.ms > to) {
      last = null;
      continue;
    }
    const x = ((p.ms - from) / (to - from)) * W;
    const y = H - 3 - ((v - min) / (max - min)) * (H - 6);
    d += `${last !== null && p.ms - last <= hold ? "L" : "M"}${x.toFixed(1)} ${y.toFixed(1)}`;
    last = p.ms;
  }
  return { d, min: Math.min(...values), max: Math.max(...values) };
}

export const SERIES = [
  {
    key: "snr",
    title: "SNR of the chosen echo",
    unit: "dB",
    digits: 1,
    hint: "A slow fall while the distance holds: a weaker echo (film on the lens, ripple, tilt).",
  },
  {
    key: "near",
    title: `Near-field energy (bins 0–${NEAR_FIELD_BINS - 1})`,
    unit: "dB over the floor",
    digits: 1,
    hint: "Fouling indicator: a film or drops on the lens raise it, slowly or in a step.",
  },
  {
    key: "echoes",
    title: "Echoes over the threshold",
    unit: "",
    digits: 0,
    hint: `Ours: ${ECHO_SNR_DB} dB over the floor. More echoes: clutter, insects or drops in the beam.`,
  },
  {
    key: "pick",
    title: "Radar's pick",
    unit: "mm",
    digits: 0,
    hint: "The distance the radar reported; gaps are status codes, no reading or no valid pick.",
  },
];

const fmt = (v, digits, unit) =>
  v === null || v === undefined
    ? "—"
    : `${v.toLocaleString(undefined, { maximumFractionDigits: digits, minimumFractionDigits: digits })}${unit ? ` ${unit}` : ""}`;

export default function RadarTrends({
  points,
  from,
  to,
  selectedMs = null,
  series = SERIES,
  empty = "Trends need at least two captures in the window.",
  className = "",
}) {
  if (points.length < 2)
    return <div className="field-radar-empty">{empty}</div>;
  const marker =
    selectedMs !== null && selectedMs >= from && selectedMs <= to
      ? ((selectedMs - from) / (to - from)) * W
      : null;
  return (
    <div className={`field-stream-trends${className ? ` ${className}` : ""}`}>
      {series.map((s) => {
        const { d, min, max } = sparkPath(points, from, to, s.key);
        const latest = [...points]
          .reverse()
          .find((p) => Number.isFinite(p[s.key]));
        return (
          <figure className="field-stream-spark" key={s.key}>
            <figcaption>
              <span>{s.title}</span>
              <b>{fmt(latest?.[s.key], s.digits, s.unit)}</b>
            </figcaption>
            <svg
              viewBox={`0 0 ${W} ${H}`}
              preserveAspectRatio="none"
              role="img"
              aria-label={`${s.title}: ${min === null ? "no values" : `${fmt(min, s.digits, s.unit)} to ${fmt(max, s.digits, s.unit)}`} over the window`}
            >
              {marker !== null && (
                <line
                  x1={marker}
                  x2={marker}
                  y1={0}
                  y2={H}
                  stroke={LIVE_COLORS.echo}
                  strokeWidth={1}
                  vectorEffect="non-scaling-stroke"
                />
              )}
              {d && (
                <path
                  d={d}
                  fill="none"
                  stroke={
                    s.color ||
                    (s.key === "pick" ? LIVE_COLORS.radar : LIVE_COLORS.ours)
                  }
                  strokeWidth={1.6}
                  strokeLinejoin="round"
                  vectorEffect="non-scaling-stroke"
                />
              )}
            </svg>
            <small>
              {min === null
                ? "No values in this window."
                : `${fmt(min, s.digits, s.unit)} – ${fmt(max, s.digits, s.unit)}`}
              {" · "}
              {s.hint}
            </small>
          </figure>
        );
      })}
    </div>
  );
}
